import { memo, type Ref } from 'react';

import { type IconComponent } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type AnnouncementAction, type AnnouncementMediaSource, type AnnouncementTone } from '../constants';
import { visibleAnnouncementActions } from '../utils/visibleAnnouncementActions';
import { AnnouncementBodyText } from './AnnouncementBodyText';
import { AnnouncementBottomButtons } from './AnnouncementBottomButtons';
import { AnnouncementCollapseButton } from './AnnouncementCollapseButton';
import { AnnouncementDismissButton } from './AnnouncementDismissButton';
import { AnnouncementGlyph } from './AnnouncementGlyph';
import { AnnouncementInlineLinks } from './AnnouncementInlineLinks';
import { AnnouncementMedia } from './AnnouncementMedia';
import { announcementTone } from './announcementTone';

export type AnnouncementBodyProps = {
  actions?: readonly AnnouncementAction[];
  body?: string | null;
  className?: string;
  /** Lets a caller move focus onto the collapse control after a layout change that would otherwise drop it. */
  collapseButtonRef?: Ref<HTMLButtonElement>;
  icon?: IconComponent;
  media?: AnnouncementMediaSource | null;
  onAction?: (action: AnnouncementAction) => void;
  onCollapse?: () => void;
  onDismiss: () => void;
  title: string;
  tone: AnnouncementTone;
};

export const AnnouncementBody = memo(
  ({
    actions,
    body,
    className,
    collapseButtonRef,
    icon,
    media,
    onAction,
    onCollapse,
    onDismiss,
    title,
    tone,
  }: AnnouncementBodyProps) => {
    // Without a handler an action click is a no-op, so hide the actions instead of rendering dead buttons.
    const visible = onAction ? visibleAnnouncementActions(actions) : [];
    const links = visible.filter((action) => action.type === 'link');
    const buttons = visible.filter((action) => action.type !== 'link');
    const bodyText = body?.trim() ? body : null;

    const content = (
      <>
        <AnnouncementGlyph icon={icon} />
        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          <div className="space-y-0.5">
            <p className="font-medium text-14" data-testid="ANNOUNCEMENT_TITLE">
              {title}
            </p>
            {bodyText || links.length > 0 ? (
              // Block markdown is not valid inside a paragraph.
              <div className="text-12 opacity-90">
                {bodyText ? <AnnouncementBodyText body={bodyText} /> : null}
                {onAction && links.length > 0 ? (
                  <div className={bodyText ? 'mt-1' : undefined}>
                    <AnnouncementInlineLinks links={links} onAction={onAction} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {onAction ? <AnnouncementBottomButtons buttons={buttons} onAction={onAction} /> : null}
        </div>
      </>
    );

    const controls = (
      // With media, -5px puts the media's top edge 40px below the card's outer edge (1px border included).
      <div className={cn('-mt-1 -mr-1 flex shrink-0 items-start justify-end', media && '-mb-[5px]')}>
        {onCollapse ? <AnnouncementCollapseButton onCollapse={onCollapse} ref={collapseButtonRef} /> : null}
        <AnnouncementDismissButton onDismiss={onDismiss} />
      </div>
    );

    return (
      <div
        className={announcementTone({
          className: cn(className, media && 'flex-col items-stretch gap-3'),
          tone,
        })}
        data-testid="ANNOUNCEMENT_CARD"
      >
        {media ? (
          // The media spans the card's full width, so the controls move to their own row above it.
          <>
            {controls}
            <AnnouncementMedia media={media} />
            <div className="flex items-start gap-2">{content}</div>
          </>
        ) : (
          <>
            {content}
            {controls}
          </>
        )}
      </div>
    );
  },
);

AnnouncementBody.displayName = 'AnnouncementBody';
